/**
 * @fileoverview useAutosave hook
 *
 * Purpose: Generic on-blur / on-commit autosave for editor surfaces.
 * Tracks the last-saved snapshot and exposes status, manual flush, and
 * retry. Designed for editors where saves are triggered by discrete
 * commits (input blur, drag stop, dropdown selection) rather than per
 * keystroke — keeping write volume low while still feeling instant.
 *
 * The hook is intentionally pull-based: it reads the current value via
 * `getValue()` at flush time. This avoids re-rendering on every keystroke
 * and lets callers source the value from refs (e.g. a TipTap editor) or
 * React state interchangeably.
 *
 * The returned callbacks (`flush`, `retry`, `resetBaseline`) are
 * identity-stable: `getValue` / `save` / `isEqual` are read through refs, so
 * a caller whose `getValue` closes over React state (and is therefore
 * recreated on every edit) can safely list them in effect dependencies
 * without the effect re-running per keystroke.
 *
 * @example
 * const autosave = useAutosave({
 *   getValue: () => ({ html: editorRef.current?.getHTML() ?? "", padding }),
 *   save: async ({ html, padding }) => updateMutation({ html, padding }),
 * });
 *
 * // On blur / commit:
 * autosave.flush();
 *
 * // Render status:
 * <AutosaveStatus status={autosave.status} onRetry={autosave.retry} />
 */

import { useCallback, useRef, useState } from "react";

export type AutosaveStatus = "idle" | "saving" | "saved" | "error";

interface UseAutosaveOptions<T> {
  /** Pull the current value at flush time. */
  getValue: () => T;
  /** Mutation to run when flushed and value differs from last save. */
  save: (value: T) => Promise<void>;
  /** Optional equality check; defaults to JSON.stringify comparison. */
  isEqual?: (a: T, b: T) => boolean;
  /**
   * Initial baseline. Saves only fire when `getValue()` differs from
   * this snapshot. If omitted, the first call to `flush()` establishes
   * the baseline by reading `getValue()` immediately.
   */
  initialValue?: T;
  /** Disable autosave (e.g. while the dialog is closed). */
  enabled?: boolean;
}

export interface UseAutosaveResult {
  status: AutosaveStatus;
  /** Timestamp (ms) of the last successful save, or null. */
  lastSavedAt: number | null;
  /** Persist the latest value if it differs from the last save. */
  flush: () => Promise<void>;
  /** Re-run the most recent failing save. */
  retry: () => Promise<void>;
  /** Mark the current value as the saved baseline (e.g. on dialog open). */
  resetBaseline: (value?: unknown) => void;
}

const defaultIsEqual = <T>(a: T, b: T) => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return a === b;
  }
};

export function useAutosave<T>({
  getValue,
  save,
  isEqual = defaultIsEqual,
  initialValue,
  enabled = true,
}: UseAutosaveOptions<T>): UseAutosaveResult {
  const lastSavedRef = useRef<T | undefined>(initialValue);
  const baselineInitialisedRef = useRef<boolean>(initialValue !== undefined);
  const [status, setStatus] = useState<AutosaveStatus>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const lastFailedValueRef = useRef<T | undefined>(undefined);

  // Read the latest callbacks through refs so flush/retry/resetBaseline keep
  // a stable identity even when callers recreate getValue/save every render
  // (e.g. getValue closing over React state).
  const getValueRef = useRef(getValue);
  getValueRef.current = getValue;
  const saveRef = useRef(save);
  saveRef.current = save;
  const isEqualRef = useRef(isEqual);
  isEqualRef.current = isEqual;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const runSave = useCallback(async (toSave: T) => {
    setStatus("saving");
    try {
      await saveRef.current(toSave);
      lastSavedRef.current = toSave;
      baselineInitialisedRef.current = true;
      lastFailedValueRef.current = undefined;
      setStatus("saved");
      setLastSavedAt(Date.now());
    } catch (err) {
      console.warn("[useAutosave] save failed", err);
      lastFailedValueRef.current = toSave;
      setStatus("error");
      throw err;
    } finally {
      inFlightRef.current = null;
    }
  }, []);

  const flush = useCallback(async () => {
    if (!enabledRef.current) return;
    const current = getValueRef.current();
    if (!baselineInitialisedRef.current) {
      lastSavedRef.current = current;
      baselineInitialisedRef.current = true;
      return;
    }
    if (
      lastSavedRef.current !== undefined &&
      isEqualRef.current(current, lastSavedRef.current)
    ) {
      return;
    }
    if (inFlightRef.current) {
      try {
        await inFlightRef.current;
      } catch {
        /* swallow; we're about to retry */
      }
      const after = getValueRef.current();
      if (
        lastSavedRef.current !== undefined &&
        isEqualRef.current(after, lastSavedRef.current)
      ) {
        return;
      }
    }
    const p = runSave(getValueRef.current()).catch(() => {
      /* status already set to "error" */
    });
    inFlightRef.current = p;
    await p;
  }, [runSave]);

  const retry = useCallback(async () => {
    if (!enabledRef.current) return;
    const toSave = lastFailedValueRef.current ?? getValueRef.current();
    const p = runSave(toSave).catch(() => {
      /* status already set to "error" */
    });
    inFlightRef.current = p;
    await p;
  }, [runSave]);

  const resetBaseline = useCallback((value?: unknown) => {
    lastSavedRef.current =
      value === undefined ? getValueRef.current() : (value as T);
    baselineInitialisedRef.current = true;
    setStatus("idle");
    setLastSavedAt(null);
    lastFailedValueRef.current = undefined;
  }, []);

  return { status, lastSavedAt, flush, retry, resetBaseline };
}
